/**
 * Forgot Password Screen
 * Password reset with email verification
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { CommonActions } from '@react-navigation/native';
import Icon from '../components/Icon';
import { authAPI } from '../services/authApi';
import { useTheme } from '../context/ThemeContext';

const ForgotPasswordScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSendOTP = async () => {
    if (!email) {
      Alert.alert('Error', 'Please enter your email address');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      console.log('📧 Sending password reset OTP...');
      await authAPI.forgotPassword(email.toLowerCase().trim());
      console.log('✅ OTP sent successfully, moving to verification step');
      // Directly move to OTP verification step
      setStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      let errorMessage = 'Failed to send OTP. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Email not found. Please check your email address.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid email format.';
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      Alert.alert('Error', 'Please enter valid 6-digit OTP');
      return;
    }

    // Move to password reset step
    setStep(3);
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      Alert.alert('Error', 'Please fill all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert('Error', 'Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      Alert.alert('Error', 'Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔐 Resetting password...');
      await authAPI.resetPassword(email.toLowerCase().trim(), otp, newPassword);
      console.log('✅ Password reset successful, navigating to Login');
      
      // Navigate to Login screen
      navigation.navigate('Login');
    } catch (error) {
      console.error('Reset password error:', error);
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid OTP or password format.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Invalid or expired OTP. Please request a new one.';
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await authAPI.forgotPassword(email.toLowerCase().trim());
      Alert.alert('Success', 'New OTP sent to your email address.');
    } catch (error) {
      console.error('Resend OTP error:', error);
      Alert.alert('Error', 'Failed to resend OTP. Please try again.');
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        {/* Title */}
        <View style={styles.titleSection}>
          <Icon name="lock-closed" size={48} color={colors.primary} />
          <Text style={[styles.title, { color: colors.text }]}>
            {step === 1 ? 'Forgot Password?' : step === 2 ? 'Verify OTP' : 'Reset Password'}
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {step === 1
              ? 'Enter your email to receive reset code'
              : step === 2
              ? 'Enter the 6-digit code sent to your email'
              : 'Create a new password for your account'}
          </Text>
        </View>

        {/* Step 1: Email Input */}
        {step === 1 && (
          <>
            <View style={styles.inputSection}>
              <Text style={[styles.label, { color: colors.text }]}>Email Address</Text>
              <View style={[styles.inputContainer, { backgroundColor: isDarkMode ? '#2A2A2A' : '#F5F5F5', borderColor: colors.border }]}>
                <TextInput
                  style={[styles.input, { color: isDarkMode ? '#FFFFFF' : '#000000' }]}
                  placeholder="someone@example.com"
                  placeholderTextColor={colors.textTertiary}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {email.length > 0 && (
                  <TouchableOpacity style={styles.iconButton} onPress={() => setEmail('')}>
                    <Icon name="close-circle" size={20} color={colors.textTertiary} />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: colors.primary }, isLoading && styles.buttonDisabled]}
              onPress={handleSendOTP}
              disabled={isLoading}>
              <Text style={[styles.primaryButtonText, { color: colors.buttonText || '#FFFFFF' }]}>
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </Text>
            </TouchableOpacity>
          </>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <>
            <View style={styles.inputSection}>
              <Text style={[styles.label, { color: colors.text }]}>Verification Code</Text>
              <View style={[styles.inputContainer, { backgroundColor: isDarkMode ? '#2A2A2A' : '#F5F5F5', borderColor: colors.border }]}>
                <TextInput
                  style={[styles.input, { color: isDarkMode ? '#FFFFFF' : '#000000' }]}
                  placeholder="000000"
                  placeholderTextColor={colors.textTertiary}
                  value={otp}
                  onChangeText={setOtp}
                  keyboardType="number-pad"
                  maxLength={6}
                  autoCapitalize="none"
                />
              </View>
            </View>

            <TouchableOpacity style={styles.resendContainer} onPress={handleResendOTP}>
              <Text style={[styles.resendText, { color: colors.textSecondary }]}>Didn't receive code? </Text>
              <Text style={[styles.resendLink, { color: colors.primary }]}>Resend</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: colors.primary }, isLoading && styles.buttonDisabled]}
              onPress={handleVerifyOTP}
              disabled={isLoading}>
              <Text style={[styles.primaryButtonText, { color: colors.buttonText || '#FFFFFF' }]}>
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </Text>
            </TouchableOpacity>
          </>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <>
            <View style={styles.inputSection}>
              <Text style={[styles.label, { color: colors.text }]}>New Password</Text>
              <View style={[styles.inputContainer, { backgroundColor: isDarkMode ? '#2A2A2A' : '#F5F5F5', borderColor: colors.border }]}>
                <TextInput
                  style={[styles.input, { color: isDarkMode ? '#FFFFFF' : '#000000' }]}
                  placeholder="••••••••"
                  placeholderTextColor={colors.textTertiary}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                />
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => setShowPassword(!showPassword)}>
                  <Icon
                    name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                    size={20}
                    color={colors.textTertiary}
                  />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputSection}>
              <Text style={[styles.label, { color: colors.text }]}>Confirm Password</Text>
              <View style={[styles.inputContainer, { backgroundColor: isDarkMode ? '#2A2A2A' : '#F5F5F5', borderColor: colors.border }]}>
                <TextInput
                  style={[styles.input, { color: isDarkMode ? '#FFFFFF' : '#000000' }]}
                  placeholder="••••••••"
                  placeholderTextColor={colors.textTertiary}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  autoCapitalize="none"
                />
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                  <Icon
                    name={showConfirmPassword ? 'eye-off-outline' : 'eye-outline'}
                    size={20}
                    color={colors.textTertiary}
                  />
                </TouchableOpacity>
              </View>
            </View>

            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: colors.primary }, isLoading && styles.buttonDisabled]}
              onPress={handleResetPassword}
              disabled={isLoading}>
              <Text style={[styles.primaryButtonText, { color: colors.buttonText || '#FFFFFF' }]}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Text>
            </TouchableOpacity>
          </>
        )}

        {/* Back to Login */}
        <TouchableOpacity
          style={styles.backToLoginContainer}
          onPress={() => navigation.navigate('Login')}>
          <Icon name="arrow-back-circle-outline" size={20} color={colors.primary} />
          <Text style={[styles.backToLoginText, { color: colors.primary }]}> Back to Login</Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 16,
  },
  backButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 40,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  inputSection: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    marginBottom: 8,
    fontWeight: '500',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 16,
    height: 56,
  },
  input: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
  },
  iconButton: {
    padding: 4,
  },
  resendContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  resendText: {
    fontSize: 14,
  },
  resendLink: {
    fontSize: 14,
    fontWeight: '600',
  },
  primaryButton: {
    borderRadius: 12,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' 
      ? {
          boxShadow: '0px 4px 8px rgba(30, 58, 95, 0.3)',
        }
      : {
          shadowColor: '#1E3A5F',
          shadowOffset: {
            width: 0,
            height: 4,
          },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          elevation: 4,
        }
    ),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  primaryButtonText: {
    fontSize: 18,
    fontWeight: '600',
  },
  backToLoginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
  },
  backToLoginText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default ForgotPasswordScreen;
